import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import { cn } from '@/app/components/ui/utils';
import { useKit, useKits } from '@/hooks/use-kits';
import type { Kit, KitStatus } from '@/lib/types';

interface KitPickerProps {
  value?: string | number | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  status?: KitStatus;
  allowNone?: boolean;
  noneLabel?: string;
  noneValue?: string;
  className?: string;
}

export function KitPicker({
  value,
  onChange,
  placeholder = 'Select kit...',
  disabled = false,
  status,
  allowNone = false,
  noneLabel = 'None',
  noneValue = '',
  className,
}: KitPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const valueString = value ? String(value) : '';

  const { data: kitsData, isLoading } = useKits({
    q: debouncedQuery,
    page_size: 50,
    status,
  });

  const selectedKitId = valueString ? Number(valueString) : 0;
  const { data: fetchedSelectedKit } = useKit(selectedKitId);

  const kits = kitsData?.results ?? [];

  const displayKit: Kit | null = useMemo(() => {
    if (!valueString) return null;
    return (
      kits.find((kit) => String(kit.id) === valueString) ||
      fetchedSelectedKit ||
      null
    );
  }, [kits, fetchedSelectedKit, valueString]);

  const handleSelect = (kitId: string) => {
    onChange(kitId);
    setOpen(false);
  };

  const displayLabel = useMemo(() => {
    if (!valueString) return placeholder;
    if (allowNone && valueString === noneValue) return noneLabel;
    if (displayKit) {
      return displayKit.name;
    }
    return placeholder;
  }, [valueString, noneValue, noneLabel, allowNone, displayKit, placeholder]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn('w-full justify-between font-normal', className)}
        >
          {isLoading && !displayKit ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </span>
          ) : (
            <span className="truncate">{displayLabel}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by kit name..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading kits...' : 'No kits found.'}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value={noneValue}
                  onSelect={() => handleSelect(noneValue)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      valueString === noneValue ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {noneLabel}
                </CommandItem>
              )}
              {kits.map((kit) => (
                <CommandItem
                  key={kit.id}
                  value={String(kit.id)}
                  onSelect={() => handleSelect(String(kit.id))}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      valueString === String(kit.id)
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{kit.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {kit.asset_count} asset
                      {kit.asset_count === 1 ? '' : 's'}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
